import type { CommandContext, CommandItem } from './CommandPalette.types';
import { Empty, Input } from 'antd';
import type { InputRef } from 'antd';
import { useEffect, useMemo, useRef } from 'react';

interface CommandPaletteProps {
  open: boolean;
  query: string;
  setQuery: (value: string) => void;
  commands: CommandItem[];
  context: CommandContext;
  activeIndex: number;
  setActiveIndex: (value: number) => void;
  onClose: () => void;
  onRun: (command: CommandItem) => void;
}

const filterAndSortCommands = (commands: CommandItem[], query: string, context: CommandContext): CommandItem[] => {
  const normalizedQuery = query.trim().toLowerCase();

  const visibleCommands = commands.filter((command) => command.visible?.(context) ?? true);

  const matchedCommands = normalizedQuery.length === 0
    ? visibleCommands
    : visibleCommands.filter((command) => {
      const fields = [
        command.title,
        command.subtitle,
        command.group,
        ...(command.keywords ?? []),
      ].filter(Boolean) as string[];

      return fields.some((field) => field.toLowerCase().includes(normalizedQuery));
    });

  return matchedCommands.sort((first, second) => {
    const firstOrder = first.order ?? Number.MAX_SAFE_INTEGER;
    const secondOrder = second.order ?? Number.MAX_SAFE_INTEGER;

    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder;
    }

    return first.title.localeCompare(second.title);
  });
};

const CommandPalette = ({
  open,
  query,
  setQuery,
  commands,
  context,
  activeIndex,
  setActiveIndex,
  onClose,
  onRun,
}: CommandPaletteProps) => {
  const inputRef = useRef<InputRef>(null);

  const filteredCommands = useMemo(() => {
    return filterAndSortCommands(commands, query, context);
  }, [commands, context, query]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveIndex(0);

    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [open, setActiveIndex]);

  useEffect(() => {
    if (activeIndex > filteredCommands.length - 1) {
      setActiveIndex(Math.max(filteredCommands.length - 1, 0));
    }
  }, [activeIndex, filteredCommands, setActiveIndex]);

  if (!open) {
    return null;
  }

  return (
    <div className='fixed inset-0 z-[1100] bg-black/35' onClick={onClose}>
      <div
        className='mx-auto mt-12 w-[min(860px,92vw)] rounded-lg border border-neutral-300 dark:border-[#555555] bg-white dark:bg-[#252526] shadow-xl'
        onClick={(event) => event.stopPropagation()}
      >
        <div className='border-b border-neutral-200 dark:border-[#404040] p-3'>
          <Input
            ref={inputRef}
            value={query}
            placeholder='Search by name...'
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActiveIndex(Math.min(activeIndex + 1, Math.max(filteredCommands.length - 1, 0)));
                return;
              }

              if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActiveIndex(Math.max(activeIndex - 1, 0));
                return;
              }

              if (event.key === 'Enter') {
                event.preventDefault();
                const selected = filteredCommands[activeIndex];
                if (!selected) {
                  return;
                }

                if (selected.enabled?.(context) === false) {
                  return;
                }

                onRun(selected);
                return;
              }

              if (event.key === 'Escape') {
                event.preventDefault();
                onClose();
              }
            }}
          />
        </div>

        <div className='max-h-[60vh] overflow-y-auto p-2'>
          {filteredCommands.length === 0 && (
            <div className='py-10'>
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='未找到可用命令' />
            </div>
          )}

          {filteredCommands.map((command, index) => {
            const enabled = command.enabled?.(context) ?? true;
            const isActive = index === activeIndex;

            return (
              <button
                key={command.id}
                type='button'
                className={`flex w-full items-start gap-3 rounded-md px-3 py-2 text-left ${isActive ? 'bg-neutral-100 dark:bg-[#333333]' : 'bg-transparent'} ${enabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => {
                  if (!enabled) {
                    return;
                  }
                  onRun(command);
                }}
              >
                <div className='mt-0.5 min-w-4'>{command.icon}</div>
                <div className='flex-1'>
                  <div className='font-medium text-neutral-900 dark:text-[#CCCCCC]'>{command.title}</div>
                  {(command.subtitle || command.group) && (
                    <div className='text-xs text-neutral-500 dark:text-[#999999]'>
                      {command.group ? `${command.group}${command.subtitle ? ' · ' : ''}` : ''}
                      {command.subtitle ?? ''}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
